import { useMemo, useState } from 'react'
import { Search, Filter } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useFetchClientesProductsUsers } from '../services/useFetchClientesProductsUsers'
import { PageLayout } from '@/layouts'
import { useCurrentUser } from '@/features/auth/state/auth'
import { useAssignAnalystProduct } from '../services/useAssignAnalystProduct'
import { toast } from '@/lib/hooks/use-toast'
import { domainConfigs } from '@/components/theme-service'
import Icon from '@/components/ui/icon'
import { formatCNPJ, formatNome } from '@/utils/format'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { getStatusColor, produtosLabels } from '../utils'
import { TypeServico } from '@/lib/enums'
import { friendlyNameMaps } from '@/components/form/input/StatusMapper'
import { cn } from '@/lib/utils'

type Analyst = {
  id: string
  name: string
  email: string
  typeUser: string
  typeCargo: string
  isActive?: boolean
  permissions?: string[]
}

type Client = {
  id: string
  cnpjcpf: string
  razao_social: string
  nome_fantasia?: string
  id_tenant: string
}

type Product = {
  id: string
  id_tenant: string
  id_user: string
  id_client: string
  id_analista?: string
  nomeAnalista?: string
  typeServico: string
  status: string
  isActive: boolean
  createdDate: string
  client: Client
}

export default function ProductManagement() {
  const { data, isLoading, isError } = useFetchClientesProductsUsers()
  const { mutate: assignAnalyst, isPending: isAssigning } = useAssignAnalystProduct()

  const NONE = '__none__'

  const clientesRaw = data?.data?.clientes ?? data?.clientes ?? []
  const analistasRaw = data?.data?.analistas ?? data?.analistas ?? []

  const user = useCurrentUser()

  const [cnpjFilter, setCnpjFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [serviceFilter, setServiceFilter] = useState<string>('all')
  const [tenantFilter, setTenantFilter] = useState<string>('all')
  const [analystFilter, setAnalystFilter] = useState<string>('all') // 'all' | 'none' | nome exato
  // NOVOS estados para edição inline
  const [editingProductId, setEditingProductId] = useState<string>('') // qual produto está em edição
  const [draftAnalystId, setDraftAnalystId] = useState<string>('') // seleção temporária do analista

  const products: Product[] = useMemo(() => {
    try {
      const out = collectProductsDeep(clientesRaw)
      const seen = new Set<string>()
      return out.filter(p => {
        if (!p.id) return true
        if (seen.has(p.id)) return false
        seen.add(p.id)
        return true
      })
    } catch {
      return []
    }
  }, [clientesRaw])

  const canSeeAll = Boolean(user?.permissions?.permission_product || user?.permissions?.permission_geral)

  const visibleProducts: Product[] = useMemo(() => {
    if (canSeeAll) return products
    const uid = String(user?.id || '')
    return products.filter(p => String(p.id_analista || '') === uid)
  }, [products, canSeeAll, user?.id])

  function getProductAnalystName(p: Product) {
    return (p.nomeAnalista || analystById.get(String(p.id_analista || '')) || '').trim()
  }

  // Opções para os selects (únicas e ordenadas)
  const serviceOptions = useMemo(
    () => Array.from(new Set(visibleProducts.map(p => p.typeServico).filter(Boolean))).sort(),
    [visibleProducts]
  )

  const tenantOptions = useMemo(
    () =>
      Array.from(new Set(visibleProducts.map(p => String(p.client?.id_tenant || '').trim()).filter(Boolean))).sort(),
    [visibleProducts]
  )

  type ClientGroup = { client: Client; products: Product[] }
  const clientGroups: ClientGroup[] = useMemo(() => {
    const byId = new Map<string, ClientGroup>()
    for (const p of visibleProducts) {
      const cid = String(p.client?.id ?? '').trim()
      if (!cid) continue
      const g = byId.get(cid) ?? { client: p.client, products: [] }
      g.products.push(p)
      byId.set(cid, g)
    }
    return Array.from(byId.values())
  }, [visibleProducts])

  function isProductLike(obj: any) {
    if (!obj || typeof obj !== 'object') return false
    const hasService = 'typeServico' in obj || 'type_servico' in obj || 'servico' in obj || 'tipo' in obj
    const hasClientLink = 'id_client' in obj || 'client' in obj
    const hasId = 'id' in obj || 'id_produto' in obj
    return !!(hasService && (hasClientLink || hasId))
  }

  function toClientRef(item: any): Client | undefined {
    if (!item || typeof item !== 'object') return undefined
    // subobjeto client
    if (item.client && typeof item.client === 'object') {
      const c = item.client
      return {
        id: c.id ?? c.id_client ?? c.idCliente ?? c.clientId ?? c.cnpjcpf ?? c.cnpj ?? '',
        cnpjcpf: c.cnpjcpf ?? c.cnpj ?? '',
        razao_social: c.razao_social ?? c.razaoSocial ?? c.nome ?? '',
        nome_fantasia: c.nome_fantasia ?? c.nomeFantasia ?? undefined,
        id_tenant: c.id_tenant ?? undefined
      }
    }
    // o próprio item é "cliente-portador"
    const isClientCarrier = 'cnpjcpf' in item || 'cnpj' in item || 'razao_social' in item || 'id_tenant' in item
    if (isClientCarrier) {
      return {
        id: item.id ?? item.id_client ?? item.idCliente ?? item.clientId ?? item.cnpjcpf ?? item.cnpj ?? '',
        cnpjcpf: item.cnpjcpf ?? item.cnpj ?? '',
        razao_social: item.razao_social ?? item.nome ?? '',
        nome_fantasia: item.nome_fantasia ?? item.nomeFantasia ?? undefined,
        id_tenant: item.id_tenant ?? undefined
      }
    }
    return undefined
  }

  function normalizeProduct(p: any, fallbackClient?: Client): Product | null {
    if (!isProductLike(p)) return null
    const client: Client | undefined =
      toClientRef(p) || (typeof p.client === 'object' ? toClientRef({ client: p.client }) : undefined) || fallbackClient

    return {
      id: p.id ?? p.id_produto ?? '',
      id_tenant: p.id_tenant ?? p.tenantId ?? p.idTenant ?? '',
      id_user: p.id_user ?? p.userId ?? p.idUser ?? '',
      id_client: p.id_client ?? p.clientId ?? p.idCliente ?? client?.id ?? '',
      id_analista: p.id_analista ?? p.analystId ?? p.idAnalista ?? undefined,
      nomeAnalista: p.nomeAnalista ?? p.analystName ?? undefined,
      typeServico: p.typeServico ?? p.type_servico ?? p.servico ?? p.tipo ?? '',
      status: p.status ?? p.situacao ?? 'PENDENTE',
      isActive: (typeof p.isActive === 'boolean' ? p.isActive : p.ativo) ?? true,
      createdDate: p.createdDate ?? p.created_at ?? p.dataCriacao ?? new Date().toISOString(),
      client: client ?? { id: '', cnpjcpf: '', razao_social: '', id_tenant: ' ' }
    }
  }

  function collectProductsDeep(node: any, fallbackClient?: Client, out: Product[] = [], seen = new Set<any>()) {
    if (!node || typeof node !== 'object') return out
    if (seen.has(node)) return out
    seen.add(node)

    // se o próprio nó parece produto
    if (isProductLike(node)) {
      const np = normalizeProduct(node, fallbackClient)
      if (np) out.push(np)
    }

    // atualiza cliente de fallback se este nó "parece cliente"
    const maybeClient = toClientRef(node) ?? fallbackClient

    // varre propriedades
    for (const key of Object.keys(node)) {
      const val = node[key]
      if (Array.isArray(val)) {
        for (const child of val) collectProductsDeep(child, maybeClient, out, seen)
      } else if (val && typeof val === 'object') {
        collectProductsDeep(val, maybeClient, out, seen)
      }
    }
    return out
  }

  const getEligibleAnalysts = (product: Product) => {
    const required = product.typeServico
    return analistas.filter(a => {
      if (a.isActive === false) return false
      if (!required) return true
      const perms = (a.permissions ?? []).map((s: string) => s.toLowerCase())
      return perms.includes(required.toLowerCase())
    })
  }

  function beginInlineEdit(product: Product) {
    setEditingProductId(String(product.id))
    setDraftAnalystId(product.id_analista ? String(product.id_analista) : NONE)
  }

  function cancelInlineEdit() {
    setEditingProductId('')
    setDraftAnalystId(NONE)
  }

  function saveInlineAssign(productId: string) {
    const payload = {
      id_product: String(productId),
      id_analista: draftAnalystId === NONE ? null : String(draftAnalystId)
    }
    assignAnalyst(payload, {
      onSuccess: res => {
        toast({
          title: 'Analista atualizado',
          description: res?.data?.nomeAnalista
            ? `Produto atribuído para ${res.data.nomeAnalista}.`
            : 'Atribuição removida (sem analista).'
        })
        cancelInlineEdit()
      },
      onError: (err: any) => {
        toast({
          variant: 'destructive',
          title: 'Falha ao atualizar',
          description: err?.message || 'Tente novamente mais tarde.'
        })
      }
    })
  }

  const analistas: Analyst[] = useMemo(
    () =>
      analistasRaw.map((a: any) => ({
        id: String(a.id ?? ''),
        name: a.name ?? a.nome ?? '',
        email: a.email ?? '',
        typeUser: a.typeUser ?? a.role ?? '',
        typeCargo: a.typeCargo ?? a.cargo ?? '',
        isActive: a.isActive ?? a.active ?? true,
        permissions: a.permissions ?? []
      })),
    [analistasRaw]
  )

  const analystById = useMemo(() => {
    const m = new Map<string, string>()
    for (const a of analistas) m.set(a.id, a.name || '')
    return m
  }, [analistas])

  const analystOptions = useMemo(() => {
    const names = new Set<string>()
    for (const p of visibleProducts) {
      const n = getProductAnalystName(p)
      if (n) names.add(n)
    }
    return Array.from(names).sort()
  }, [visibleProducts, analystById])

  type ProductRow = { client: Client; product: Product }

  const productRows: ProductRow[] = useMemo(() => {
    // parte 1: respeita os filtros que você já tinha (cnpj/status ao nível do cliente)
    let base = clientGroups

    if (cnpjFilter) {
      const q = cnpjFilter.toLowerCase()
      base = base.filter(
        g =>
          (g.client.cnpjcpf || '').toLowerCase().includes(q) || (g.client.razao_social || '').toLowerCase().includes(q)
      )
    }
    if (statusFilter !== 'all') {
      base = base.filter(g => g.products.some(p => p.status === statusFilter))
    }

    // parte 2: achata em linhas de produto
    let rows: ProductRow[] = []
    for (const g of base) {
      for (const p of g.products) {
        rows.push({ client: g.client, product: p })
      }
    }

    // parte 3: NOVOS FILTROS por produto
    if (serviceFilter !== 'all') {
      rows = rows.filter(r => r.product.typeServico === serviceFilter)
    }

    if (tenantFilter !== 'all') {
      rows = rows.filter(r => r.client.id_tenant === tenantFilter)
    }

    if (analystFilter !== 'all') {
      if (analystFilter === 'none') {
        rows = rows.filter(r => !getProductAnalystName(r.product))
      } else {
        rows = rows.filter(r => getProductAnalystName(r.product) === analystFilter)
      }
    }

    return rows
  }, [clientGroups, cnpjFilter, statusFilter, serviceFilter, tenantFilter, analystFilter])

  const getTenantTitle = (tenantId?: string) =>
    (tenantId && domainConfigs[tenantId]?.title) || domainConfigs.default.title || tenantId || ''

  const DEFAULTS = {
    cnpj: '',
    status: 'all',
    service: 'all',
    tenant: 'all',
    analyst: 'all'
  }

  const hasActiveFilters = useMemo(() => {
    return (
      cnpjFilter.trim() !== DEFAULTS.cnpj ||
      statusFilter !== DEFAULTS.status ||
      serviceFilter !== DEFAULTS.service ||
      tenantFilter !== DEFAULTS.tenant ||
      analystFilter !== DEFAULTS.analyst
    )
  }, [cnpjFilter, statusFilter, serviceFilter, tenantFilter, analystFilter])

  function resetFilters() {
    setCnpjFilter(DEFAULTS.cnpj)
    setStatusFilter(DEFAULTS.status)
    setServiceFilter(DEFAULTS.service)
    setTenantFilter(DEFAULTS.tenant)
    setAnalystFilter(DEFAULTS.analyst)
  }

  function ActiveFilterChips() {
    const chips: { label: string; onClear: () => void }[] = []

    if (cnpjFilter.trim()) chips.push({ label: `CNPJ/Razão: ${cnpjFilter.trim()}`, onClear: () => setCnpjFilter('') })
    if (serviceFilter !== 'all')
      chips.push({
        label: `Serviço: ${produtosLabels[serviceFilter as TypeServico]}`,
        onClear: () => setServiceFilter('all')
      })
    if (tenantFilter !== 'all')
      chips.push({ label: `Empresa: ${getTenantTitle(tenantFilter)}`, onClear: () => setTenantFilter('all') })
    if (analystFilter !== 'all')
      chips.push({
        label: analystFilter === 'none' ? 'Analista: (sem)' : `Analista: ${formatNome(analystFilter)}`,
        onClear: () => setAnalystFilter('all')
      })
    if (statusFilter !== 'all')
      chips.push({ label: `Status: ${statusFilter.replace(/_/g, ' ')}`, onClear: () => setStatusFilter('all') })

    if (!chips.length) return null
    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {chips.map((c, i) => (
          <Badge key={i} variant="secondary" className="gap-1">
            {c.label}
            <button
              type="button"
              onClick={c.onClear}
              className="ml-1 text-muted-foreground hover:opacity-80"
              aria-label="remover filtro"
            >
              ×
            </button>
          </Badge>
        ))}
      </div>
    )
  }

  if (isLoading) return <div className="p-6">Carregando…</div>
  if (isError) return <div className="p-6 text-destructive">Erro ao carregar dados.</div>

  return (
    <PageLayout
      title="Gestão de Analistas"
      description={<p className="text-muted-foreground">Gerencie e designe analistas responsáveis</p>}
    >
      {/* Filtros */}
      <div className="space-y-3">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filtros
            </CardTitle>
            <CardDescription>Use os filtros para encontrar produtos rapidamente</CardDescription>
          </CardHeader>

          <CardContent className="space-y-3">
            {/* TOOLBAR COMPACTA */}
            <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
              {/* Esquerda: Busca principal */}
              <div className="w-full md:max-w-md relative">
                <Label className="sr-only">CNPJ / Razão Social</Label>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Buscar por CNPJ ou razão social..."
                  value={cnpjFilter}
                  onChange={e => setCnpjFilter(e.target.value)}
                  className="pl-9 h-8"
                />
              </div>

              {/* Direita: controles rápidos */}
              <div className="flex flex-wrap items-end gap-2">
                {/* Serviço (rápido) */}
                <div className="min-w-[180px]">
                  <Label className="sr-only">Empresa</Label>

                  <Select value={tenantFilter} onValueChange={setTenantFilter}>
                    <SelectTrigger className="h-8 bg-background ">
                      <SelectValue placeholder="Selecione a empresa" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas as empresas</SelectItem>
                      {tenantOptions.map(tid => (
                        <SelectItem key={String(tid)} value={String(tid)}>
                          {getTenantTitle(String(tid))}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Popover / Dropdown com filtros adicionais */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-8">
                      Mais filtros
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-[320px]">
                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label>Tipo de Serviço</Label>
                        <Select value={serviceFilter} onValueChange={setServiceFilter}>
                          <SelectTrigger className="h-8">
                            <SelectValue placeholder="Serviço" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos os serviços</SelectItem>
                            {serviceOptions.map(s => (
                              <SelectItem key={s} value={s}>
                                {produtosLabels[s as TypeServico]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {user?.permissions.permission_geral ||
                        (user?.permissions.permission_geral && (
                          <div className="space-y-1">
                            <Label>Analista</Label>
                            <Select value={analystFilter} onValueChange={setAnalystFilter}>
                              <SelectTrigger className="h-8">
                                <SelectValue placeholder="Selecione o analista" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">Todos</SelectItem>
                                <SelectItem value="none">Sem analista</SelectItem>
                                {analystOptions.map(n => (
                                  <SelectItem key={n} value={n}>
                                    {formatNome(n)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        ))}

                      <div className="flex justify-end pt-1">
                        <Button
                          variant="secondary"
                          size="sm"
                          className="h-8 bg-primary text-foreground-secondary hover:primary-hover"
                          onClick={resetFilters}
                          disabled={!hasActiveFilters}
                        >
                          Limpar filtros
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Limpar na toolbar */}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 bg-primary text-foreground-secondary hover:primary-hover"
                  onClick={resetFilters}
                  disabled={!hasActiveFilters}
                >
                  Limpar
                </Button>
              </div>
            </div>

            {/* Chips dos filtros ativos */}
            <ActiveFilterChips />
          </CardContent>
        </Card>

        {/* Tabela */}

        <Card>
          <CardHeader>
            <CardTitle>Lista de Produtos ({productRows.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className=" mt-3 max-h-[400px] overflow-y-auto border rounded-md">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>CNPJ</TableHead>
                    <TableHead>Produto</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Analista</TableHead>
                    {user?.permissions.update_user_product && <TableHead>Ações</TableHead>}
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {productRows.map(({ client, product }) => {
                    return (
                      <TableRow key={`${client.id}-${product.id}`}>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="font-medium">{client.razao_social}</div>
                            {client.nome_fantasia && (
                              <div className="text-xs text-muted-foreground">{client.nome_fantasia}</div>
                            )}
                          </div>
                        </TableCell>

                        <TableCell className="text-sm ">{formatCNPJ(client.cnpjcpf)}</TableCell>

                        <TableCell className="text-sm">
                          {(produtosLabels[product.typeServico as TypeServico] || product.typeServico).replace(
                            /_/g,
                            ' '
                          )}
                        </TableCell>

                        <TableCell>
                          <span
                            className={cn(
                              'flex items-center justify-between min-w-[220px] text-sm px-3 py-1 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2',
                              'transition-colors',
                              getStatusColor(product.status)
                            )}
                          >
                            {friendlyNameMaps['produtosStatus']?.[product.status]}
                          </span>
                        </TableCell>

                        <TableCell className="align-middle">
                          {editingProductId === product.id ? (
                            <div className="flex items-center gap-2">
                              <Select value={draftAnalystId ?? NONE} onValueChange={setDraftAnalystId}>
                                <SelectTrigger className="min-w-[220px]">
                                  <SelectValue placeholder="Escolha um analista" />
                                </SelectTrigger>
                                <SelectContent>
                                  {/* sentinela para limpar */}
                                  <SelectItem value={NONE}>Nenhum (limpar)</SelectItem>

                                  {getEligibleAnalysts(product).map(a => (
                                    <SelectItem key={a.id} value={a.id}>
                                      {formatNome(a.name)}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>

                              <Button size="sm" onClick={() => saveInlineAssign(product.id)} disabled={isAssigning}>
                                {isAssigning ? 'Salvando…' : 'Salvar'}
                              </Button>

                              <Button size="sm" variant="outline" onClick={cancelInlineEdit} disabled={isAssigning}>
                                Cancelar
                              </Button>
                            </div>
                          ) : (product.nomeAnalista || '').trim() ? (
                            <span className="text-sm">{formatNome(product.nomeAnalista)}</span>
                          ) : (
                            <span className="text-muted-foreground text-sm">Não designado</span>
                          )}
                        </TableCell>
                        {user?.permissions.update_user_product && (
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              onClick={() => beginInlineEdit(product)}
                              disabled={editingProductId === product.id}
                            >
                              <Icon name="ArrowRightLeft" className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  )
}
